import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Activity, TrendingUp, TrendingDown, Minus, Target, Compass, LineChart as LineChartIcon, Sparkles, Loader2, Lock, Shield, Grid2X2 } from "lucide-react";
import api from "@/lib/api";
import { effectiveIsClinician } from "@/lib/effectiveClinician";
import { WorkPatternsCard } from "@/components/voicemood/WorkPatternsCard";
import { SocialPatternsCard } from "@/components/voicemood/SocialPatternsCard";
import { AmbivalenceCard } from "@/components/voicemood/AmbivalenceCard";

// =================== 1. WELLBEING COMPOSITE GAUGE ===================
const _wellbeingReading = (score, trend) => {
  if (score === null || score === undefined) return "";
  let band = "";
  if (score >= 70) band = "Sei in una fase di benessere alto: i tuoi momenti tendono a essere nutrienti e ben gestiti";
  else if (score >= 55) band = "Sei in una fase di benessere medio-alto: le situazioni in cui ti trovi sono mediamente gestibili";
  else if (score >= 40) band = "Sei in una fase intermedia: ci sono momenti nutrienti e altri più faticosi che si bilanciano";
  else if (score >= 25) band = "Sei in una fase di benessere basso: i momenti pesanti o difficili sono prevalenti";
  else band = "Sei in una fase difficile: la quota di momenti pesanti è alta";
  const trendNote = trend === "ascendente"
    ? " — l'andamento delle ultime 2 settimane è in miglioramento."
    : trend === "discendente"
      ? " — l'andamento delle ultime 2 settimane è in peggioramento."
      : trend === "stabile"
        ? " — l'andamento si è stabilizzato rispetto alle 2 settimane precedenti."
        : ".";
  return band + trendNote;
};

export const WellbeingGauge = ({ isClinician = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/voice-mood/wellbeing-index?days=90")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <SkeletonCard title="Indice di benessere" />;
  if (!data) return null;

  const hasToday = data.today_avg !== null && data.today_avg !== undefined;
  const score = data.today_avg ?? data.last_7_days_avg;
  const trend = data.trend_label;
  const delta = data.trend_delta;
  const TrendIcon = trend === "ascendente" ? TrendingUp : trend === "discendente" ? TrendingDown : Minus;
  const trendColor = trend === "ascendente" ? "text-emerald-600" : trend === "discendente" ? "text-rose-600" : "text-slate-500";

  // Delta odierno vs media della settimana — usato nel blocco OGGI
  const deltaTodayVs7 = (hasToday && data.last_7_days_avg !== null)
    ? Math.round((data.today_avg - data.last_7_days_avg) * 10) / 10
    : null;

  // 2026-05-28 — Maturity-based rendering (opzione b): hide numeric score until N≥10.
  // Beta testers see only a qualitative label until the index is "Stabile". Clinicians
  // see the raw numbers regardless (it's their job to read partial data).
  const maturity = data.maturity || "stable";
  const showNumericScore = isClinician || maturity === "stable";
  const maturityCopy = {
    no_data:   "Nessun check-in registrato negli ultimi 90 giorni.",
    exploring: `Indice in esplorazione · ${data.total_scored}/${data.min_n_threshold} check-in. Ancora pochi dati per leggere un valore numerico affidabile.`,
    forming:   `Pattern in formazione · ${data.total_scored}/${data.min_n_threshold} check-in. Il valore numerico verrà sbloccato a partire da ${data.min_n_threshold} check-in.`,
    stable:    null,
  };
  const maturityBadgeColor = {
    no_data:   "bg-slate-100 text-slate-600",
    exploring: "bg-amber-50 text-amber-800 border border-amber-200",
    forming:   "bg-sky-50 text-sky-800 border border-sky-200",
    stable:    "bg-emerald-50 text-emerald-800 border border-emerald-200",
  }[maturity];

  return (
    <Card className="border-2 border-rose-100" data-testid="wellbeing-gauge-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Activity className="w-4 h-4 text-rose-500" />
          Indice di benessere
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!showNumericScore ? (
          <div className="space-y-2" data-testid="wellbeing-maturity-state">
            <span
              className={`inline-block text-[11px] font-medium px-2 py-0.5 rounded-full ${maturityBadgeColor}`}
              data-testid="wellbeing-maturity-badge"
            >
              {data.maturity_label}
            </span>
            <p className="text-xs text-foreground/80 leading-relaxed">
              {maturityCopy[maturity]}
            </p>
            {data.total_scored > 0 && (
              <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-rose-400 transition-all"
                  style={{ width: `${Math.min(100, (data.total_scored / data.min_n_threshold) * 100)}%` }}
                />
              </div>
            )}
          </div>
        ) : score === null || score === undefined ? (
          <p className="text-xs text-muted-foreground italic">
            Servono almeno alcuni check-in per calcolare il tuo indice.
          </p>
        ) : (
          <>
            {/* OGGI — gerarchia visiva primaria */}
            <div className="bg-rose-50/70 border border-rose-100 rounded-md p-3" data-testid="wellbeing-today-block">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] uppercase tracking-wider font-semibold text-rose-700">Indice di oggi</span>
                <span className="text-[10px] text-muted-foreground">
                  {hasToday ? `${data.today_n} check-in` : "nessun check-in"}
                </span>
              </div>
              {hasToday ? (
                <div className="flex items-baseline gap-2">
                  <span className="text-5xl font-bold text-rose-600" data-testid="wellbeing-score">
                    {data.today_avg}
                  </span>
                  <span className="text-base text-muted-foreground">/ 100</span>
                  {deltaTodayVs7 !== null && (
                    <span
                      className={`ml-auto text-[11px] font-medium ${deltaTodayVs7 > 0 ? "text-emerald-600" : deltaTodayVs7 < 0 ? "text-rose-600" : "text-slate-500"}`}
                      data-testid="wellbeing-today-delta"
                    >
                      {deltaTodayVs7 > 0 ? "▲" : deltaTodayVs7 < 0 ? "▼" : "="} {deltaTodayVs7 > 0 ? "+" : ""}{deltaTodayVs7} vs settimana
                    </span>
                  )}
                </div>
              ) : (
                <p className="text-xs text-foreground/80 italic mt-1" data-testid="wellbeing-no-today">
                  Non hai ancora registrato check-in oggi. L'indice di oggi apparirà qui dopo il primo check-in della giornata.
                </p>
              )}
            </div>

            {/* SETTIMANA e MESE — 2 card affiancate */}
            <div className="mt-3 grid grid-cols-2 gap-2" data-testid="wellbeing-history">
              <div className="border border-slate-200 rounded px-3 py-2" data-testid="wellbeing-week">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Indice settimanale</div>
                <div className="flex items-baseline gap-1 mt-0.5">
                  <span className="text-2xl font-semibold text-slate-800">{data.last_7_days_avg ?? "—"}</span>
                  {data.last_7_days_avg !== null && <span className="text-xs text-muted-foreground">/100</span>}
                </div>
                <div className="text-[10px] text-muted-foreground/80">
                  ultimi 7gg · {data.last_7_days_n ?? 0} check-in
                </div>
              </div>

              <div className="border border-slate-200 rounded px-3 py-2" data-testid="wellbeing-month">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Indice a 30 giorni</div>
                <div className="flex items-baseline gap-1 mt-0.5">
                  <span className="text-2xl font-semibold text-slate-800">{data.last_30_days_avg ?? "—"}</span>
                  {data.last_30_days_avg !== null && <span className="text-xs text-muted-foreground">/100</span>}
                </div>
                <div className="text-[10px] text-muted-foreground/80">
                  ultimi 30gg · {data.last_30_days_n ?? 0} check-in
                </div>
              </div>
            </div>

            {/* TENDENZA — label esplicita + sparkline più grande */}
            {data.series && data.series.length >= 2 && (
              <div className="mt-4" data-testid="wellbeing-trend-block">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-700">
                    Tendenza · ultimi {data.series.length} giorni
                  </span>
                  {trend && (
                    <span className={`flex items-center gap-1 text-[11px] font-medium ${trendColor}`} data-testid="wellbeing-trend">
                      <TrendIcon className="w-3 h-3" />
                      {trend === "ascendente" ? "in miglioramento" : trend === "discendente" ? "in peggioramento" : "stabile"}
                      {delta !== null && delta !== undefined && ` (${delta > 0 ? "+" : ""}${delta})`}
                    </span>
                  )}
                </div>
                <MiniSparkline series={data.series} />
                <div className="flex justify-between text-[9px] text-muted-foreground/70 mt-0.5">
                  <span>0</span>
                  <span>scala 0–100</span>
                  <span>100</span>
                </div>
              </div>
            )}

            <p className="text-xs leading-snug text-foreground/90 mt-3" data-testid="wellbeing-reading">
              {_wellbeingReading(score, trend)}
            </p>
            {isClinician ? (
              <p className="text-[10px] text-muted-foreground italic mt-1">
                Sintesi pesata delle 6 dimensioni del check-in. Più alto = più nutriente. [Clinico] maturity={maturity} · N={data.total_scored} · today_n={data.today_n}
              </p>
            ) : (
              <p className="text-[10px] text-muted-foreground italic mt-1" data-testid="wellbeing-source">
                Fonte scientifica: Diener & Kahneman, esperienza momentanea del benessere.
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

const MiniSparkline = ({ series }) => {
  if (!series || series.length < 2) return null;
  const values = series.slice(-30).map((d) => d.wellbeing);
  const max = Math.max(...values, 100);
  const min = Math.min(...values, 0);
  const range = max - min || 1;
  const w = 240;
  const h = 40;
  const step = w / (values.length - 1 || 1);
  const points = values.map((v, i) => `${i * step},${h - ((v - min) / range) * h}`).join(" ");
  return (
    <svg width={w} height={h} className="mt-3 overflow-visible" data-testid="wellbeing-sparkline">
      <polyline fill="none" stroke="#e11d48" strokeWidth="2" points={points} />
      {values.map((v, i) => (
        <circle key={`pt-${i}`} cx={i * step} cy={h - ((v - min) / range) * h} r="1.8" fill="#e11d48" />
      ))}
    </svg>
  );
};

// =================== 2. WEEKLY NARRATIVE SUMMARY ===================
export const WeeklySummaryCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/voice-mood/weekly-summary")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <SkeletonCard title="La tua settimana" />;
  if (!data) return null;

  return (
    <Card className="border-2 border-violet-100 bg-gradient-to-br from-violet-50/40 to-white" data-testid="weekly-summary-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-violet-600" />
          La tua settimana
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!data.unlocked ? (
          <div className="flex items-start gap-2 text-xs text-muted-foreground">
            <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>{data.message || "Compila qualche check-in in più per sbloccare il riassunto settimanale."}</span>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-sm leading-relaxed" data-testid="weekly-narrative">{data.narrative}</p>
            {data.notable_pattern && (
              <p className="text-xs italic text-violet-700 border-l-2 border-violet-300 pl-2" data-testid="weekly-pattern">
                ✨ {data.notable_pattern}
              </p>
            )}
            <div className="text-[10px] text-muted-foreground pt-1">
              {data.n_entries} check-in · benessere medio {data.wellbeing_avg ?? "—"} · {data.week_key}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// =================== 3. KARASEK QUADRANTS PER ACTIVITY ===================
const KARASEK_LABELS = {
  strain: {
    label: "Sotto pressione",
    color: "bg-rose-500",
    text: "text-rose-700",
    desc: "molto impegnativo, ma senti di non riuscire a gestirlo",
  },
  active: {
    label: "Sfida attiva",
    color: "bg-emerald-500",
    text: "text-emerald-700",
    desc: "molto impegnativo, ma senti di gestirlo bene (zona di crescita)",
  },
  passive: {
    label: "In stallo",
    color: "bg-slate-400",
    text: "text-slate-600",
    desc: "poco impegnativo e poca agency, rischio di disinvestimento",
  },
  low_strain: {
    label: "A tuo agio",
    color: "bg-sky-500",
    text: "text-sky-700",
    desc: "poco impegnativo e ben gestibile, riposante",
  },
};

// Build a clinically-integrated reading for an activity.
// Critical clinical principle (Laura, May 2026): a quadrant alone is misleading.
// 30% "strain" can be lived as "sfida tonica" (piacevole + carica) — qualitatively
// the opposite of "strain depressivo" (spiacevole + scarica). The reading MUST
// integrate pleasure × arousal directly, not hide them in a drill-down.
//
// Returns { main, social } strings. `main` reads the dominant quadrant THROUGH
// the affective composition of its episodes. `social` reads where it happens.
const _karasekReading = (act, isClinician = false) => {
  const dom = act.dominant;
  const actName = act.activity.replace(/_/g, " ");
  const domQ = act.quadrants?.[dom] || {};
  const domPct = act[`${dom}_pct`] || 0;
  const sub = domQ.sub_pct || {};

  // Aggregate affective quality of the dominant quadrant (only its episodes)
  const positive = (sub.pleasant_energized || 0) + (sub.pleasant_low || 0);
  const negative = (sub.unpleasant_energized || 0) + (sub.unpleasant_low || 0);
  const topSubKey = Object.entries(sub)
    .filter(([k]) => k !== "neutral")
    .sort((a, b) => b[1] - a[1])[0]?.[0];
  const topSubPct = sub[topSubKey] || 0;

  // Quadrant-name in natural language (no clinical jargon for beta testers)
  const NATURAL_DOM = {
    strain:    "i tuoi momenti più impegnativi",
    active:    "i tuoi momenti di sfida attiva",
    passive:   "i tuoi momenti di basso coinvolgimento",
    low_strain: "i tuoi momenti più gestibili e leggeri",
  };
  const domNat = NATURAL_DOM[dom] || "i tuoi momenti";

  // Build the main reading by combining dominant quadrant + affective signature
  let main = "";

  // Case A: dominant quadrant has a clearly POSITIVE affective signature
  if (positive >= 50 && negative <= 25) {
    const POS = {
      strain:     `La maggior parte di **${actName}** (${domPct}%) la vivi nei tuoi momenti più impegnativi — e quasi sempre con un buon sapore: piacere ed energia restano presenti (${positive.toFixed(0)}% di questi momenti). È fatica viva, non logorante: vale la pena riconoscertelo e proteggerla.`,
      active:     `La maggior parte di **${actName}** (${domPct}%) la vivi in sfida attiva, e con un'esperienza interna positiva (${positive.toFixed(0)}%): senti di starci dentro con piacere ed energia. Questi sono momenti nutrienti, non solo "produttivi".`,
      passive:    `La maggior parte di **${actName}** (${domPct}%) la vivi in momenti di basso coinvolgimento — ma vissuti bene (${positive.toFixed(0)}% con piacere): è recupero vero, non vuoto. Riconoscerlo come ristoro è importante.`,
      low_strain: `La maggior parte di **${actName}** (${domPct}%) la vivi in una zona leggera e gestibile, e con piacere ed energia (${positive.toFixed(0)}%): è ricarica autentica, uno dei tuoi spazi-risorsa.`,
    };
    main = POS[dom];
  }
  // Case B: dominant quadrant has a clearly NEGATIVE affective signature
  else if (negative >= 45) {
    const NEG = {
      strain:     `La maggior parte di **${actName}** (${domPct}%) la vivi nei tuoi momenti più impegnativi, e con un sapore prevalentemente faticoso (${negative.toFixed(0)}% senza piacere o scarica). È il segnale a cui prestare più attenzione: c'è qualcosa che potresti spostare, delegare o reincorniciare?`,
      active:     `La maggior parte di **${actName}** (${domPct}%) la vivi in sfida attiva, ma a un costo: nel ${negative.toFixed(0)}% di questi momenti manca piacere o energia. Gestisci, ma quanto è sostenibile?`,
      passive:    `La maggior parte di **${actName}** (${domPct}%) la vivi in momenti di basso coinvolgimento, e perlopiù spenti (${negative.toFixed(0)}%). Piccoli innesti di scelta o di stimolo possono spostare l'ago.`,
      low_strain: `La maggior parte di **${actName}** (${domPct}%) la vivi in una zona apparentemente leggera, ma con una quota di disagio (${negative.toFixed(0)}%): non è ristoro vero. Vale la pena darci un nome.`,
    };
    main = NEG[dom];
  }
  // Case C: mixed — most informative for clinician but balanced for users
  else {
    const MIX = {
      strain:     `La maggior parte di **${actName}** (${domPct}%) la vivi nei tuoi momenti più impegnativi, con un mix di sfumature: né solo fatica, né solo flusso. Vale la pena guardare cosa fa la differenza fra una giornata e l'altra.`,
      active:     `La maggior parte di **${actName}** (${domPct}%) la vivi in sfida attiva, con sfumature varie — energia presente, ma il piacere oscilla. Notarne le variazioni può aiutare a capire dove queste sfide ti nutrono davvero.`,
      passive:    `La maggior parte di **${actName}** (${domPct}%) la vivi in basso coinvolgimento, con sfumature miste — alcuni momenti di riposo, altri più spenti. La differenza è qualitativa, non quantitativa.`,
      low_strain: `La maggior parte di **${actName}** (${domPct}%) la vivi in una zona leggera, con sfumature varie. È utile distinguere fra il riposo vero e una zona di comfort un po' troppo lontana dalle sfide.`,
    };
    main = MIX[dom];
  }

  // Clinician-only technical line (load × control fingerprint)
  let technical = null;
  if (isClinician) {
    const parts = ["strain", "active", "low_strain", "passive"]
      .map((k) => `${k} ${act[`${k}_pct`]}%`)
      .join(" · ");
    const subLabel = topSubKey
      ? ` Sub-profilo affettivo dominante nel ${dom}: ${topSubKey.replace(/_/g, " ")} (${topSubPct}%).`
      : "";
    technical = `[Clinico] Distribuzione carico × controllo: ${parts}.${subLabel}`;
  }

  // Social reading for the dominant quadrant (most relevant for reflection)
  const breakdown = (domQ.social_breakdown || []).filter((s) => s.pct >= 5);
  let social = null;
  if (breakdown.length > 0) {
    const top = breakdown[0];
    const topLabel = (SOCIAL_CONTEXT_LABELS[top.context] || top.context).toLowerCase();
    const SOC = {
      strain:     `La maggior parte di questi momenti impegnativi li vivi ${topLabel} (${top.pct}%)`,
      active:     `Queste sfide attive emergono soprattutto ${topLabel} (${top.pct}%)`,
      passive:    `Il basso coinvolgimento si concentra ${topLabel} (${top.pct}%)`,
      low_strain: `Questi momenti leggeri li vivi prevalentemente ${topLabel} (${top.pct}%)`,
    };
    social = SOC[dom] || "";
    if (breakdown.length >= 2 && breakdown[1].pct >= 20) {
      const second = breakdown[1];
      const secondLabel = (SOCIAL_CONTEXT_LABELS[second.context] || second.context).toLowerCase();
      social += `, ma anche ${secondLabel} (${second.pct}%)`;
    }
    social += ".";
  }

  return { main, social, technical, breakdown };
};

// Build a clinically rich reading for a single quadrant's affective drill-down.
// This is the "nested drill-down" Laura asked for: it explains HOW the episodes
// of a given Karasek quadrant are actually being lived (pleas × vitality).
// (Removed 2026-05-28: superseded by integrated _karasekReading.)

// Friendly Italian labels for social_context values (matches backend taxonomy)
const SOCIAL_CONTEXT_LABELS = {
  solo: "Da solo/a",
  amici: "Con amici",
  partner: "Con il/la partner",
  famiglia: "Con la famiglia",
  colleghi: "Con i colleghi",
  superiori: "Con un superiore",
  clienti: "Con clienti / utenti",
  altri: "Con altri",
  partner_famiglia: "Con partner o famiglia",
  con_altri: "Con altri",
};

export const KarasekCard = ({ isClinician = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/voice-mood/karasek-by-activity")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  const title = isClinician ? "Carico × controllo per attività" : "Come vivi i tuoi ambiti";
  const subtitle = isClinician
    ? "Come vivi ogni ambito quando senti carico (impegno) e controllo (capacità di gestione)"
    : "Una lettura di come si distribuiscono i tuoi momenti, ambito per ambito.";

  if (loading) return <SkeletonCard title={title} />;
  if (!data) return null;
  const activities = data.activities || [];

  return (
    <Card className="border" data-testid="karasek-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Target className="w-4 h-4 text-emerald-600" />
          {title}
        </CardTitle>
        <p className="text-[10px] text-muted-foreground">{subtitle}</p>
      </CardHeader>
      <CardContent>
        {activities.length === 0 ? (
          <div className="flex items-start gap-2 text-xs text-muted-foreground">
            <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>Servono almeno 10 check-in per ciascuna attività per generare la lettura.</span>
          </div>
        ) : (
          <div className="space-y-4" data-testid="karasek-activities">
            {activities.map((act) => {
              const reading = _karasekReading(act, isClinician);
              const breakdown = reading.breakdown;
              return (
                <div key={act.activity} className="border rounded-md p-3 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold capitalize">{act.activity.replace(/_/g, " ")}</span>
                    {isClinician && <span className="text-[10px] text-muted-foreground">N={act.n}</span>}
                  </div>

                  {/* Stacked bar — visual signature of the activity */}
                  <div className="flex h-3 rounded-sm overflow-hidden">
                    {["strain", "active", "low_strain", "passive"].map((k) => {
                      const pct = act[`${k}_pct`];
                      if (pct < 1) return null;
                      return (
                        <div key={k} className={KARASEK_LABELS[k].color} style={{ width: `${pct}%` }} title={`${KARASEK_LABELS[k].label}: ${pct}%`} />
                      );
                    })}
                  </div>

                  {/* MAIN reading — integrates load × control × pleasure × arousal in one paragraph */}
                  <p
                    className="text-xs leading-relaxed text-foreground/90"
                    data-testid={`karasek-reading-${act.activity}`}
                    dangerouslySetInnerHTML={{ __html: reading.main.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>") }}
                  />

                  {/* SOCIAL reading — first the narrative line, then a small percentage list */}
                  {reading.social && (
                    <div className="space-y-1" data-testid={`karasek-social-${act.activity}`}>
                      <p className="text-xs leading-relaxed text-foreground/80">
                        {reading.social}
                      </p>
                      {breakdown.length > 1 && (
                        <p className="text-[10px] text-muted-foreground leading-relaxed">
                          {breakdown.map((s, i) => (
                            <span key={s.context}>
                              {i > 0 && " · "}
                              {(SOCIAL_CONTEXT_LABELS[s.context] || s.context)} {s.pct}%
                            </span>
                          ))}
                        </p>
                      )}
                    </div>
                  )}

                  {/* CLINICIAN-only technical fingerprint (load × control distribution + dominant sub-profile) */}
                  {isClinician && reading.technical && (
                    <p
                      className="text-[10px] text-muted-foreground italic leading-relaxed border-t border-dashed border-slate-200 pt-2"
                      data-testid={`karasek-technical-${act.activity}`}
                    >
                      {reading.technical}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// =================== 4. SDT RADAR PER ACTIVITY ===================
// Build a concrete reading of an SDT profile (autonomy × competence × relatedness).
const _sdtReading = (act) => {
  const a = act.autonomy ?? 0;
  const c = act.competence ?? 0;
  const r = act.relatedness;
  const actName = act.activity.replace(/_/g, " ");
  const hi = (v) => v !== null && v !== undefined && v >= 60;
  const lo = (v) => v !== null && v !== undefined && v <= 35;

  // Lead with the strongest signal
  const parts = [];
  if (hi(a) && hi(c)) parts.push(`Quando fai ${actName} senti di averlo scelto tu e di saperlo gestire: è un'area di autodeterminazione piena`);
  else if (hi(a) && lo(c)) parts.push(`Quando fai ${actName} senti che è una tua scelta, ma fai fatica a gestirlo: l'autonomia c'è, la padronanza meno`);
  else if (lo(a) && hi(c)) parts.push(`Quando fai ${actName} sai gestirlo bene, ma non lo senti pienamente tuo — lo subisci più che sceglierlo`);
  else if (lo(a) && lo(c)) parts.push(`${actName.charAt(0).toUpperCase() + actName.slice(1)} è un'area difficile per te: né la senti tua né la gestisci con sicurezza`);
  else parts.push(`${actName.charAt(0).toUpperCase() + actName.slice(1)} ti coinvolge in modo medio: né piena autodeterminazione né piena alienazione`);

  // Relatedness nuance
  if (r !== null && r !== undefined) {
    if (hi(r)) parts.push("le relazioni in cui sei coinvolta in questi momenti sono prevalentemente nutrienti");
    else if (lo(r)) parts.push("le relazioni in cui sei coinvolta in questi momenti tendono a essere fatiche o tese");
  }
  return parts.join("; ") + ".";
};

export const SdtRadarCard = ({ isClinician = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/voice-mood/sdt-radar")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <SkeletonCard title="Profilo motivazionale per attività" />;
  if (!data) return null;
  const activities = data.activities || [];

  return (
    <Card className="border" data-testid="sdt-radar-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Compass className="w-4 h-4 text-amber-600" />
          Profilo motivazionale per attività
        </CardTitle>
        <p className="text-[10px] text-muted-foreground">
          {isClinician
            ? "Tre bisogni psicologici di base: scelta (autonomia), capacità (padronanza), qualità relazionale"
            : "Tre dimensioni di ogni ambito: quanto lo scegli, quanto lo gestisci, come vivi le persone"}
        </p>
      </CardHeader>
      <CardContent>
        {activities.length === 0 ? (
          <div className="flex items-start gap-2 text-xs text-muted-foreground">
            <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>Servono almeno 15 check-in per ciascuna attività per generare la lettura.</span>
          </div>
        ) : (
          <div className="space-y-3" data-testid="sdt-activities">
            {activities.map((act) => (
              <div key={act.activity} className="border rounded-md p-3 space-y-2">
                <div className="flex items-start gap-3">
                  <RadarSvg a={act.autonomy} c={act.competence} r={act.relatedness} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold capitalize">{act.activity.replace(/_/g, " ")}</p>
                      <span className="text-[10px] text-muted-foreground">N={act.n}</span>
                    </div>
                    <div className="text-[11px] grid grid-cols-3 gap-2 mt-1">
                      <div>
                        <p className="text-amber-700 font-medium">Scelta</p>
                        <p className="text-sm font-bold">{act.autonomy}%</p>
                      </div>
                      <div>
                        <p className="text-emerald-700 font-medium">Capacità</p>
                        <p className="text-sm font-bold">{act.competence}%</p>
                      </div>
                      <div>
                        <p className="text-sky-700 font-medium">Relazione</p>
                        <p className="text-sm font-bold">{act.relatedness !== null ? `${act.relatedness}%` : "—"}</p>
                      </div>
                    </div>
                  </div>
                </div>
                <p className="text-xs leading-snug text-foreground/90" data-testid={`sdt-reading-${act.activity}`}>
                  {_sdtReading(act)}
                </p>
                <details className="text-[11px]">
                  <summary className="text-muted-foreground cursor-pointer hover:text-foreground">
                    Cosa significano queste tre dimensioni?
                  </summary>
                  <div className="mt-2 space-y-1 text-[10px] text-muted-foreground">
                    <p>🟡 <strong>Scelta (autonomia)</strong>: % di momenti in cui senti di aver scelto pienamente di essere in quella situazione.</p>
                    <p>🟢 <strong>Capacità (padronanza)</strong>: % di momenti in cui senti di gestirla totalmente.</p>
                    <p>🔵 <strong>Relazione</strong>: tra i momenti con persone, quanti vivi come positivi.</p>
                  </div>
                </details>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const RadarSvg = ({ a = 0, c = 0, r = 0 }) => {
  const cx = 36, cy = 36;
  const radius = 28;
  // 3 vertices at -90°, 30°, 150°
  const angles = [-Math.PI / 2, Math.PI / 6, (5 * Math.PI) / 6];
  const ringPts = angles.map((ang) => `${cx + radius * Math.cos(ang)},${cy + radius * Math.sin(ang)}`).join(" ");
  const vals = [a, c, r ?? 0];
  const dataPts = angles.map((ang, i) => {
    const v = (vals[i] || 0) / 100;
    return `${cx + radius * v * Math.cos(ang)},${cy + radius * v * Math.sin(ang)}`;
  }).join(" ");
  return (
    <svg width={72} height={72} viewBox="0 0 72 72" className="shrink-0">
      <polygon points={ringPts} fill="none" stroke="#e5e7eb" strokeWidth="1" />
      <polygon points={dataPts} fill="rgba(245, 158, 11, 0.3)" stroke="#f59e0b" strokeWidth="1.5" />
      {angles.map((ang, i) => (
        <circle key={`v-${i}`} cx={cx + radius * (vals[i] || 0) / 100 * Math.cos(ang)} cy={cy + radius * (vals[i] || 0) / 100 * Math.sin(ang)} r="2" fill="#f59e0b" />
      ))}
    </svg>
  );
};

// =================== 5. TREND + RECOVERY ===================
export const TrendRecoveryCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/voice-mood/trend-recovery")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <SkeletonCard title="Trend e ripresa" />;
  if (!data) return null;

  return (
    <Card className="border" data-testid="trend-recovery-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <LineChartIcon className="w-4 h-4 text-blue-600" />
          Trend e capacità di ripresa
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!data.unlocked ? (
          <div className="flex items-start gap-2 text-xs text-muted-foreground">
            <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>Mancano <strong>{data.n_until_unlock}</strong> check-in per analizzare i tuoi pattern di trend e ripresa.</span>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="text-[11px] text-muted-foreground">
              Linea di base personale: <strong className="text-foreground">{data.baseline}</strong>/100
            </div>
            {data.rolling_avg_series && <RollingChart series={data.rolling_avg_series} baseline={data.baseline} />}
            <div className="border-t pt-2 text-xs space-y-1" data-testid="recovery-stats">
              <p>Episodi pesanti rilevati: <strong>{data.n_episodes}</strong></p>
              {data.avg_recovery_steps !== null && (
                <p>
                  Tempo medio per tornare al tuo equilibrio dopo un episodio pesante:{" "}
                  <strong className="text-blue-700">{data.avg_recovery_steps} check-in</strong>
                </p>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const RollingChart = ({ series, baseline }) => {
  if (!series || series.length < 2) return null;
  const data = series.slice(-60);
  const w = 280;
  const h = 70;
  const vals = data.map((d) => d.rolling_avg);
  const max = Math.max(...vals, 100, baseline);
  const min = Math.min(...vals, 0, baseline);
  const range = max - min || 1;
  const step = w / (data.length - 1 || 1);
  const pts = data.map((d, i) => `${i * step},${h - ((d.rolling_avg - min) / range) * h}`).join(" ");
  const baselineY = h - ((baseline - min) / range) * h;
  return (
    <svg width={w} height={h} className="overflow-visible">
      <line x1="0" y1={baselineY} x2={w} y2={baselineY} stroke="#94a3b8" strokeDasharray="2 3" strokeWidth="1" />
      <polyline points={pts} fill="none" stroke="#2563eb" strokeWidth="2" />
    </svg>
  );
};

// =================== shared skeleton ===================
const SkeletonCard = ({ title }) => (
  <Card className="border" data-testid="derived-skeleton">
    <CardHeader className="pb-2">
      <CardTitle className="text-sm flex items-center gap-2">
        <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />
        {title}
      </CardTitle>
    </CardHeader>
    <CardContent>
      <div className="h-12 bg-muted/40 rounded animate-pulse" />
    </CardContent>
  </Card>
);

// =================== 6. EQUANIMITY INDEX ===================
const _equanimityReading = (data) => {
  const score = data.equanimity_score;
  let band = "";
  if (score >= 60) band = "Anche sotto carico pesante mantieni un livello di piacevolezza alto: hai una buona capacità di reggere senza farti travolgere";
  else if (score >= 40) band = "Sotto carico pesante mantieni una piacevolezza media: c'è una certa tenuta emotiva, ma c'è anche margine di crescita";
  else band = "Sotto carico pesante la piacevolezza scende molto: le situazioni impegnative tendono a sopraffarti emotivamente";
  if (data.drop_light_to_heavy !== null && data.drop_light_to_heavy !== undefined) {
    const drop = data.drop_light_to_heavy;
    if (drop > 4) band += `. Il calo tra momenti leggeri e momenti pesanti è marcato (${drop} punti su 10)`;
    else if (drop < 1) band += `. Il calo tra momenti leggeri e pesanti è minimo — la tua tenuta è notevole`;
  }
  return band + ".";
};

export const EquanimityCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/voice-mood/equanimity")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <SkeletonCard title="Resilienza affettiva" />;
  if (!data) return null;

  return (
    <Card className="border" data-testid="equanimity-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Shield className="w-4 h-4 text-indigo-600" />
          Resilienza affettiva
        </CardTitle>
        <p className="text-[10px] text-muted-foreground">La tua capacità di resistere sotto carico elevato</p>
      </CardHeader>
      <CardContent>
        {!data.unlocked ? (
          <div className="flex items-start gap-2 text-xs text-muted-foreground">
            <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>Servono almeno 10 check-in con carico pesante (ora: {data.n_heavy}).</span>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold text-indigo-600">{data.equanimity_score}</span>
              <span className="text-sm text-muted-foreground">/ 100</span>
            </div>

            {/* Drop visibile: piacevolezza media leggero vs pesante */}
            <div className="grid grid-cols-2 gap-2" data-testid="equanimity-drop-block">
              <div className="border border-slate-200 rounded px-2.5 py-1.5">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Carico leggero</div>
                <div className="text-base font-semibold text-slate-800 mt-0.5">
                  {data.avg_pleas_light ?? "—"}<span className="text-xs text-muted-foreground font-normal">/10</span>
                </div>
                <div className="text-[10px] text-muted-foreground/80">piacevolezza media</div>
              </div>
              <div className="border border-slate-200 rounded px-2.5 py-1.5">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Carico pesante</div>
                <div className="text-base font-semibold text-slate-800 mt-0.5">
                  {data.avg_pleas_heavy}<span className="text-xs text-muted-foreground font-normal">/10</span>
                </div>
                <div className="text-[10px] text-muted-foreground/80">piacevolezza media</div>
              </div>
            </div>

            {data.drop_light_to_heavy !== null && data.drop_light_to_heavy !== undefined && (
              <div
                className="flex items-center justify-between bg-slate-50 rounded px-2.5 py-1.5"
                data-testid="equanimity-drop"
              >
                <span className="text-[11px] text-muted-foreground">
                  Calo dal carico leggero al pesante
                </span>
                <span className={`text-sm font-bold ${
                  data.drop_light_to_heavy > 3 ? "text-rose-600"
                  : data.drop_light_to_heavy > 1 ? "text-amber-600"
                  : "text-emerald-600"
                }`}>
                  {data.drop_light_to_heavy > 0 ? "−" : "+"}{Math.abs(data.drop_light_to_heavy)}
                </span>
              </div>
            )}

            <p className="text-xs leading-snug text-foreground/90" data-testid="equanimity-reading">
              {_equanimityReading(data)}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// =================== 7. RUSSELL CIRCUMPLEX (4 quadrants) ===================
const CIRCUMPLEX_LABELS = {
  flow: { label: "Flow", desc: "ti senti bene e attivata, coinvolta in quello che fai", color: "bg-emerald-500", text: "text-emerald-700" },
  serenity: { label: "Serenità", desc: "ti senti bene e calma, in uno stato di quiete", color: "bg-sky-500", text: "text-sky-700" },
  agitation: { label: "Agitazione", desc: "ti senti attivata ma a disagio: tensione, urgenza, ansia", color: "bg-amber-500", text: "text-amber-700" },
  apathy: { label: "Apatia", desc: "ti senti giù e senza energia: stanchezza, ritiro, tristezza", color: "bg-slate-500", text: "text-slate-700" },
};

// Revela observational reference ranges (Piccinini, 2026 — proprio IP).
// These are NOT clinical targets: they are descriptive ranges on a ≥4-week window
// that, in the pilot, are associated with adaptive functioning. Individual
// variability is high; we never present these as norms to hit.
const CIRCUMPLEX_REVELA_RANGES = {
  flow: { lo: 25, hi: 40, tooLow: "limitato", tooHigh: "sostenuto" },
  serenity: { lo: 35, hi: 50, tooLow: "limitato", tooHigh: "molto presente" },
  agitation: { lo: 5, hi: 15, tooLow: "molto basso", tooHigh: "elevato" },
  apathy: { lo: 5, hi: 15, tooLow: "molto basso", tooHigh: "elevato" },
};

// Returns "within" | "below" | "above" | "near_lo" | "near_hi"
const _rangeStatus = (pct, range) => {
  const { lo, hi } = range;
  const margin = 3; // pp of tolerance around the band
  if (pct < lo - margin) return "below";
  if (pct > hi + margin) return "above";
  if (pct < lo) return "near_lo";
  if (pct > hi) return "near_hi";
  return "within";
};

// Build a personalized reading of the circumplex distribution.
const _circumplexReading = (data) => {
  const items = [
    { k: "flow", pct: data.flow_pct },
    { k: "serenity", pct: data.serenity_pct },
    { k: "agitation", pct: data.agitation_pct },
    { k: "apathy", pct: data.apathy_pct },
  ].sort((a, b) => b.pct - a.pct);
  const top = items[0];
  const second = items[1];
  const positive = (data.flow_pct || 0) + (data.serenity_pct || 0);
  const negative = (data.agitation_pct || 0) + (data.apathy_pct || 0);
  const HEADLINES = {
    flow: `Il tuo stato emotivo prevalente è di flow (${top.pct}%): nei tuoi momenti ti senti coinvolta in quello che fai e attiva positivamente`,
    serenity: `Il tuo stato emotivo prevalente è di serenità (${top.pct}%): nei tuoi momenti tendi a stare bene in una modalità tranquilla`,
    agitation: `Il tuo stato emotivo prevalente è di agitazione (${top.pct}%): ti senti spesso attivata ma a disagio`,
    apathy: `Il tuo stato emotivo prevalente è di apatia (${top.pct}%): ti senti spesso bassa di tono e priva di energia`,
  };
  let line = HEADLINES[top.k] || "";

  if (second && second.pct >= 20) {
    const NUANCES = {
      flow: ` con buoni momenti anche di coinvolgimento attivo (${second.pct}%)`,
      serenity: ` con momenti di calma (${second.pct}%)`,
      agitation: ` ma una quota di tensione/agitazione (${second.pct}%) è presente`,
      apathy: ` con momenti di disinvestimento (${second.pct}%)`,
    };
    line += NUANCES[second.k] || "";
  }
  line += ".";

  // Balance comment — only trustworthy when BOTH classes are non-trivial.
  // If one side is ~0, saying "si bilanciano" is wrong.
  const NEAR_ZERO = 5; // pct below which we treat as "no presence" of that pole
  if (positive >= 60 && negative <= 30) {
    line += ` In sintesi: la quota di benessere (flow+serenità=${positive.toFixed(0)}%) prevale nettamente su quella di disagio (${negative.toFixed(0)}%).`;
  } else if (negative >= 50) {
    line += ` In sintesi: il disagio emotivo (agitazione+apatia=${negative.toFixed(0)}%) è una quota significativa dei tuoi momenti — vale la pena guardarci dentro.`;
  } else if (positive <= NEAR_ZERO && negative <= NEAR_ZERO) {
    line += ` In sintesi: i tuoi momenti si concentrano in zone affettivamente neutre o miste; pochi episodi nettamente positivi o negativi.`;
  } else if (negative <= NEAR_ZERO) {
    line += ` In sintesi: gli stati positivi (${positive.toFixed(0)}%) sono presenti, mentre quelli di disagio sono praticamente assenti.`;
  } else if (positive <= NEAR_ZERO) {
    line += ` In sintesi: gli stati di disagio (${negative.toFixed(0)}%) emergono, mentre quelli nettamente positivi sono praticamente assenti.`;
  } else {
    line += ` In sintesi: stati positivi (${positive.toFixed(0)}%) e di disagio (${negative.toFixed(0)}%) coesistono in quote simili nei tuoi momenti.`;
  }
  return line;
};

// =================== 2026-02-17 (Laura): "STORIA EMOTIVA" ===================
// Sostituiscono la lettura clinica con un linguaggio narrativo per l'utente.
//
// 1) La scoperta principale — frase forte basata sulla distribuzione
const _mainDiscovery = (data) => {
  const flow = data.flow_pct || 0;
  const seren = data.serenity_pct || 0;
  const agit = data.agitation_pct || 0;
  const apat = data.apathy_pct || 0;
  const pos = flow + seren;
  const neg = agit + apat;
  if (pos >= 70 && flow >= seren + 10) {
    return "Il tuo benessere nasce soprattutto dal coinvolgimento, più che dal riposo.";
  }
  if (pos >= 70 && seren >= flow + 10) {
    return "Il tuo benessere nasce soprattutto dalla calma, più che dall'attivazione.";
  }
  if (pos >= 70) {
    return "Quando stai bene, alterni naturalmente momenti di coinvolgimento e momenti di calma.";
  }
  if (neg >= 40 && agit >= apat + 10) {
    return "I momenti di disagio che vivi sono più spesso legati a tensione/agitazione che a apatia.";
  }
  if (neg >= 40 && apat >= agit + 10) {
    return "I momenti di disagio che vivi sono più spesso legati a stanchezza/sotto-tono che a tensione.";
  }
  if (flow >= seren + 10) {
    return "Tendi a stare meglio quando sei attiva che quando sei semplicemente rilassata.";
  }
  if (seren >= flow + 10) {
    return "Tendi a stare meglio nei momenti di calma che in quelli di attivazione.";
  }
  return "Il tuo equilibrio emotivo è composito: piacere e attivazione si combinano in modi diversi.";
};

// 2) Sintesi narrativa — paragrafo di apertura
const _narrativeSummary = (data) => {
  const n = data.n_total;
  const flow = data.flow_pct || 0;
  const seren = data.serenity_pct || 0;
  const agit = data.agitation_pct || 0;
  const apat = data.apathy_pct || 0;
  const pos = flow + seren;
  const neg = agit + apat;
  const items = [
    { k: "flow", pct: flow, phrase: "coinvolta e attiva" },
    { k: "serenity", pct: seren, phrase: "serena e rilassata" },
    { k: "agitation", pct: agit, phrase: "in tensione" },
    { k: "apathy", pct: apat, phrase: "sotto-tono" },
  ].sort((a, b) => b.pct - a.pct);
  const first = items[0];
  const second = items[1];

  let lead;
  if (pos >= 60) {
    lead = `Negli ultimi ${n} momenti registrati, la maggior parte delle tue esperienze si colloca in una zona di benessere.`;
  } else if (neg >= 40) {
    lead = `Negli ultimi ${n} momenti registrati, una quota significativa delle tue esperienze attraversa zone di disagio.`;
  } else {
    lead = `Negli ultimi ${n} momenti registrati, le tue esperienze si distribuiscono tra benessere e disagio in modo misto.`;
  }
  const secondPart = second && second.pct >= 15 ? ` oppure ${second.phrase} (${second.pct}%)` : "";
  const closing = neg < 15 ? ` I momenti di disagio risultano poco frequenti (${neg.toFixed(0)}%).` : "";
  return `${lead} Ti senti spesso ${first.phrase} (${first.pct}% dei momenti)${secondPart}.${closing}`;
};

// 3) Clima emotivo — metafora meteo
const _weatherFor = (pct) => {
  if (pct >= 40) return { icon: "☀️", label: "prevalente" };
  if (pct >= 20) return { icon: "🌤️", label: "frequente" };
  if (pct >= 5) return { icon: "🌥️", label: "occasionale" };
  return { icon: "⛈️", label: "rara" };
};

// 4) Profilo automatico — etichetta + descrizione
const _autoProfile = (data) => {
  const flow = data.flow_pct || 0;
  const seren = data.serenity_pct || 0;
  const agit = data.agitation_pct || 0;
  const apat = data.apathy_pct || 0;
  if (flow >= 40) {
    return {
      icon: "🌱",
      label: "Coinvolta",
      desc: `Il flow rappresenta una parte importante dei tuoi momenti registrati (${flow}%). Tendi a stare bene quando sei impegnata in attività che ti assorbono e ti danno energia.`,
    };
  }
  if (seren >= 40) {
    return {
      icon: "🌊",
      label: "Serena",
      desc: `Il benessere emerge soprattutto nei momenti di calma e recupero (${seren}% di serenità).`,
    };
  }
  if (flow >= 20 && agit >= 15) {
    return {
      icon: "⚡",
      label: "Intensa",
      desc: `Alta presenza sia di flow (${flow}%) sia di agitazione (${agit}%). Vivi i momenti con intensità: quando funzionano, ti coinvolgono profondamente; quando no, generano tensione.`,
    };
  }
  if (apat >= 25) {
    return {
      icon: "🍂",
      label: "Sotto-tono",
      desc: `Una quota rilevante dei tuoi momenti (${apat}%) si colloca in zona di stanchezza/disinvestimento. Vale la pena guardarci dentro insieme.`,
    };
  }
  return {
    icon: "🔀",
    label: "Composito",
    desc: `I tuoi momenti si distribuiscono in modo equilibrato tra i quattro stati. Non c'è un'esperienza dominante: il tuo profilo è ricco e variegato.`,
  };
};

// 5) Narrativa "Cosa raccontano i tuoi dati"
const _dataNarrative = (data) => {
  const flow = data.flow_pct || 0;
  const seren = data.serenity_pct || 0;
  const agit = data.agitation_pct || 0;
  const apat = data.apathy_pct || 0;
  if (flow >= 35 && seren >= 25) {
    return "Nei tuoi check-in emerge un profilo caratterizzato soprattutto da coinvolgimento e serenità. Quando stai bene, non sembri cercare soltanto tranquillità: il benessere compare spesso nei momenti in cui sei attiva e coinvolta in ciò che fai.";
  }
  if (flow >= 35) {
    return "Nei tuoi check-in il coinvolgimento attivo (flow) è la cifra più presente del tuo benessere. I momenti in cui ti senti dentro a ciò che fai sono quelli che ti restituiscono più energia positiva.";
  }
  if (seren >= 40) {
    return "Nei tuoi check-in il benessere passa soprattutto attraverso la calma: i momenti di quiete e distensione sono quelli in cui ti riconosci di più. L'attivazione, quando arriva, sembra meno centrale per il tuo equilibrio.";
  }
  if (agit >= 20 && apat < 15) {
    return "Nei tuoi check-in emerge una buona dose di attivazione, ma spesso vissuta come tensione anziché come coinvolgimento. Vale la pena guardare cosa fa la differenza tra i tuoi momenti di flow e quelli di agitazione: ambiente, persone, tipo di attività.";
  }
  if (apat >= 20) {
    return "Nei tuoi check-in i momenti di disinvestimento o stanchezza occupano una quota significativa. Possono essere parte di una fase fisiologica (carico, sonno, salute) o segnalare qualcosa di più profondo da nominare insieme.";
  }
  return "I tuoi check-in raccontano un equilibrio composito: piacere e attivazione si combinano in modi diversi a seconda del contesto. La parte sotto ti aiuta a vedere quali ambienti e attività fanno la differenza.";
};

export const CircumplexCard = ({ isClinician = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/voice-mood/circumplex")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <SkeletonCard title="Mappa affettiva" />;
  if (!data) return null;

  // Distribuzione (per la lista "Come si distribuiscono i tuoi momenti?")
  const distribution = data.unlocked ? [
    { k: "flow", color: "🟢", label: "Flow", pct: data.flow_pct, desc: "Quando ti senti coinvolta, energica e interessata." },
    { k: "serenity", color: "🟦", label: "Serenità", pct: data.serenity_pct, desc: "Quando stai bene senza particolare attivazione." },
    { k: "agitation", color: "🟠", label: "Agitazione", pct: data.agitation_pct, desc: "Quando hai energia ma il momento è vissuto negativamente." },
    { k: "apathy", color: "⚫", label: "Apatia", pct: data.apathy_pct, desc: "Quando hai poca energia e poco piacere." },
  ] : [];

  const profile = data.unlocked ? _autoProfile(data) : null;

  return (
    <Card className="border" data-testid="circumplex-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Grid2X2 className="w-4 h-4 text-purple-600" />
          Mappa affettiva
        </CardTitle>
        <p className="text-[10px] text-muted-foreground">
          {isClinician
            ? "Come si distribuiscono i tuoi momenti combinando piacere e attivazione (vitalità)"
            : "Come si distribuiscono i tuoi momenti tra piacere ed energia"}
        </p>
      </CardHeader>
      <CardContent>
        {!data.unlocked ? (
          <div className="flex items-start gap-2 text-xs text-muted-foreground">
            <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>Servono almeno 5 check-in con vitalità per disegnare la mappa.</span>
          </div>
        ) : (
          <div className="space-y-4">
            {/* ✨ LA SCOPERTA PRINCIPALE — frase forte in evidenza */}
            <div className="rounded-md border border-amber-200 bg-amber-50/60 p-3" data-testid="circumplex-discovery">
              <p className="text-[10px] uppercase tracking-wider text-amber-700/80 font-semibold mb-1">
                ✨ La scoperta principale
              </p>
              <p className="text-sm font-medium text-stone-800 leading-snug">
                {_mainDiscovery(data)}
              </p>
            </div>

            {/* 🌿 IL TUO EQUILIBRIO EMOTIVO — sintesi narrativa */}
            <div data-testid="circumplex-summary">
              <p className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold mb-1.5">
                🌿 Il tuo equilibrio emotivo
              </p>
              <p className="text-xs leading-relaxed text-foreground/90">
                {_narrativeSummary(data)}
              </p>
            </div>

            {/* Come si distribuiscono i tuoi momenti? — lista */}
            <div data-testid="circumplex-distribution">
              <p className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold mb-1.5">
                Come si distribuiscono i tuoi momenti?
              </p>
              <ul className="space-y-1.5">
                {distribution.map((d) => (
                  <li key={d.k} className="flex items-start gap-2 text-xs leading-snug" data-testid={`circumplex-dist-${d.k}`}>
                    <span className="shrink-0">{d.color}</span>
                    <span className="flex-1">
                      <strong className="text-stone-800">{d.label}</strong>
                      <span className="text-stone-500"> — {d.pct}%</span>
                      <span className="block text-stone-500 text-[11px] mt-0.5">{d.desc}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* 🌤️ CLIMA EMOTIVO — metafora meteo */}
            <div data-testid="circumplex-weather">
              <p className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold mb-1.5">
                🌤️ Il tuo clima emotivo
              </p>
              <ul className="grid grid-cols-2 gap-1.5">
                {distribution.map((d) => {
                  const w = _weatherFor(d.pct);
                  return (
                    <li key={`w-${d.k}`} className="flex items-center gap-1.5 text-xs" data-testid={`circumplex-weather-${d.k}`}>
                      <span>{w.icon}</span>
                      <span className="text-stone-700">{d.label}</span>
                      <span className="text-stone-400">→ {w.label}</span>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* ⬆️⬇️ RISPETTO AL MESE SCORSO — solo se entrambe le finestre sono unlock */}
            {data.deltas && (
              <div data-testid="circumplex-mom">
                <p className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold mb-1.5">
                  Rispetto al mese scorso
                </p>
                <ul className="grid grid-cols-2 gap-1.5">
                  {[
                    { k: "flow", label: "Flow" },
                    { k: "serenity", label: "Serenità" },
                    { k: "agitation", label: "Agitazione" },
                    { k: "apathy", label: "Apatia" },
                  ].map((d) => {
                    const delta = data.deltas[d.k];
                    const positive = d.k === "flow" || d.k === "serenity";
                    const improved = positive ? delta > 0 : delta < 0;
                    const stable = Math.abs(delta) < 2;
                    // Freccia = direzione del valore (⬆️ aumenta, ⬇️ diminuisce, ➡️ stabile)
                    const icon = stable ? "➡️" : delta > 0 ? "⬆️" : "⬇️";
                    const sign = delta > 0 ? "+" : "";
                    // Colore = se la variazione è favorevole o no
                    const color = stable ? "text-stone-500" : improved ? "text-emerald-700" : "text-rose-700";
                    return (
                      <li key={`mom-${d.k}`} className="flex items-center gap-1.5 text-xs" data-testid={`circumplex-mom-${d.k}`}>
                        <span>{icon}</span>
                        <span className="text-stone-700">{d.label}</span>
                        <span className={`font-medium ${color}`}>{sign}{delta}%</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
            {!data.deltas && data.previous_window && !data.previous_window.unlocked && (
              <p className="text-[10px] text-muted-foreground italic" data-testid="circumplex-mom-locked">
                Il confronto con il mese precedente si attiverà appena avrai abbastanza dati anche nella finestra precedente (almeno 5 check-in).
              </p>
            )}

            {/* 🌱 PROFILO PREVALENTE */}
            {profile && (
              <div className="rounded-md border border-emerald-200 bg-emerald-50/50 p-3" data-testid="circumplex-profile">
                <p className="text-[10px] uppercase tracking-wider text-emerald-700/80 font-semibold mb-1">
                  Profilo prevalente
                </p>
                <p className="text-sm font-medium text-stone-800 leading-snug mb-1">
                  {profile.icon} {profile.label}
                </p>
                <p className="text-xs text-stone-600 leading-relaxed">{profile.desc}</p>
              </div>
            )}

            {/* 📖 COSA RACCONTANO I TUOI DATI */}
            <div data-testid="circumplex-narrative">
              <p className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold mb-1.5">
                📖 Cosa raccontano i tuoi dati
              </p>
              <p className="text-xs leading-relaxed text-foreground/90">
                {_dataNarrative(data)}
              </p>
            </div>

            {/* ============ VISTA CLINICO: range Revela, grid 2x2, fonte ============ */}
            {isClinician && (
              <div className="space-y-3 pt-3 border-t border-stone-200" data-testid="circumplex-clinician-block">
                <p className="text-[10px] uppercase tracking-wider text-purple-700 font-semibold">
                  Vista clinico — Riferimenti Revela & lettura tecnica
                </p>
                <details className="rounded-md bg-stone-50/60 border border-stone-200" data-testid="circumplex-explainer">
                  <summary className="cursor-pointer list-none flex items-center justify-between gap-2 px-2.5 py-2 text-[11px] font-medium text-stone-700 hover:text-stone-900">
                    <span>Come si legge questa mappa?</span>
                    <span className="text-[10px] text-stone-500">leggi</span>
                  </summary>
                  <div className="px-2.5 pb-2.5 pt-1 text-[11px] text-stone-600 leading-relaxed space-y-2">
                    <p>
                      La mappa divide i tuoi momenti in <strong>quattro stati emotivi</strong>,
                      combinando due cose: quanto ti senti <em>bene o male</em> e quanto ti
                      senti <em>energica o spenta</em>.
                    </p>
                    <ul className="space-y-1.5 list-none pl-0">
                      <li className="flex gap-1.5">
                        <span>🟢</span>
                        <span><strong>Flow</strong> — stai bene <em>e</em> sei attiva: coinvolta in
                        quello che fai, energia che scorre. Tipico di attività che ti
                        appassionano o ti fanno sentire &laquo;nella tua dimensione&raquo;.</span>
                      </li>
                      <li className="flex gap-1.5">
                        <span>🔵</span>
                        <span><strong>Serenità</strong> — stai bene ma in <em>quiete</em>:
                        calma, distensione, relax. Tipica di momenti di riposo o pause.</span>
                      </li>
                      <li className="flex gap-1.5">
                        <span>🟡</span>
                        <span><strong>Agitazione</strong> — sei attiva <em>ma a disagio</em>:
                        tensione, urgenza, ansia, irritabilità. Il corpo è in moto ma
                        senza piacere.</span>
                      </li>
                      <li className="flex gap-1.5">
                        <span>⚪</span>
                        <span><strong>Apatia</strong> — stai sotto-tono <em>e</em> senza
                        energia: stanchezza, demotivazione, ritiro, malinconia.</span>
                      </li>
                    </ul>
                    <p className="text-stone-500 italic">
                      Non esiste una distribuzione &laquo;giusta&raquo;: ognuno ha il suo equilibrio.
                      Quello che conta è capire <strong>cosa fa la differenza</strong> tra i
                      tuoi momenti di flow/serenità e quelli di agitazione/apatia — la
                      parte sotto ti aiuta a vederlo.
                    </p>
                  </div>
                </details>

                <div className="grid grid-cols-2 gap-1.5">
                  <QuadrantBox q="serenity" pct={data.serenity_pct} />
                  <QuadrantBox q="flow" pct={data.flow_pct} />
                  <QuadrantBox q="apathy" pct={data.apathy_pct} />
                  <QuadrantBox q="agitation" pct={data.agitation_pct} />
                </div>
                <p className="text-xs leading-snug text-foreground/90" data-testid="circumplex-reading">
                  {_circumplexReading(data)}
                </p>
                <p className="text-[10px] text-muted-foreground italic">
                  {data.n_total} momenti analizzati · {data.mixed_mid_pct}% momenti misti/neutri
                </p>
                <details className="text-[10px]" data-testid="circumplex-reference-details">
                  <summary className="text-slate-500 cursor-pointer hover:text-slate-700 font-light tracking-wide">
                    Cos'è il riferimento Revela?
                  </summary>
                  <p className="mt-1.5 text-[10px] text-muted-foreground italic leading-relaxed">
                    I range mostrati (Flow 25–40%, Serenità 35–50%, Agitazione e Apatia 5–15%) sono <strong>riferimenti
                    osservazionali Revela</strong>, non target da raggiungere. Si applicano su finestre di almeno
                    4 settimane di check-in. La variabilità individuale è alta: una persona introversa può vivere
                    meno momenti di flow e star bene, un periodo di esami può alzare l'agitazione senza che sia
                    patologico.
                  </p>
                </details>
                <p className="text-[10px] text-muted-foreground italic" data-testid="circumplex-source">
                  Fonte scientifica: Russell (1980), Circumplex Model of Affect · Riferimento Revela: Piccinini (2026).
                </p>
              </div>
            )}

            {!isClinician && (
              <p className="text-[10px] text-muted-foreground italic">
                {data.n_total} momenti analizzati.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const QuadrantBox = ({ q, pct }) => {
  const info = CIRCUMPLEX_LABELS[q];
  const range = CIRCUMPLEX_REVELA_RANGES[q];
  const status = _rangeStatus(pct, range);

  // Color of the status dot — tells the user how they sit vs Revela reference
  const statusColor = {
    within: "bg-emerald-500",
    near_lo: "bg-amber-400",
    near_hi: "bg-amber-400",
    below: "bg-slate-400",
    above: "bg-rose-400",
  }[status];

  const statusLabel = {
    within: "Nel riferimento Revela",
    near_lo: "Appena sotto il riferimento",
    near_hi: "Appena sopra il riferimento",
    below: `Sotto il riferimento (${range.tooLow})`,
    above: `Sopra il riferimento (${range.tooHigh})`,
  }[status];

  // Bar shows the USER's percentage, on a 0–60% scale (since quadrants rarely
  // exceed ~60%; this keeps the Revela reference markers visually meaningful).
  // If pct = 0, the bar is empty.
  const SCALE_MAX = 60;
  const clamp = (n) => Math.max(0, Math.min(100, n));
  const userBarWidth = clamp((pct / SCALE_MAX) * 100);
  const tickLo = clamp((range.lo / SCALE_MAX) * 100);
  const tickHi = clamp((range.hi / SCALE_MAX) * 100);

  // Bar fill color follows the quadrant accent (no more deceptive green).
  const fillColor = info.color; // e.g., "bg-sky-500" for serenity

  return (
    <div className="border rounded-md p-2.5" data-testid={`circumplex-${q}`}>
      <div className="flex items-center justify-between">
        <span className={`text-xs font-semibold ${info.text}`}>{info.label}</span>
        <span className="text-base font-bold">{pct}%</span>
      </div>

      {/* User's percentage bar with Revela reference markers as thin ticks */}
      <div className="mt-2" data-testid={`circumplex-band-${q}`}>
        <div className="relative h-2 bg-slate-100 rounded-full overflow-hidden">
          {/* User's actual percentage — empty if pct = 0 */}
          <div
            className={`absolute top-0 bottom-0 left-0 ${fillColor} rounded-full transition-all`}
            style={{ width: `${userBarWidth}%` }}
            title={`La tua quota: ${pct}%`}
          />
          {/* Revela reference tick marks (thin neutral lines, not a green fill) */}
          <div
            className="absolute top-0 bottom-0 w-px bg-slate-400/80"
            style={{ left: `${tickLo}%` }}
            title={`Riferimento Revela: da ${range.lo}%`}
          />
          <div
            className="absolute top-0 bottom-0 w-px bg-slate-400/80"
            style={{ left: `${tickHi}%` }}
            title={`Riferimento Revela: a ${range.hi}%`}
          />
        </div>
        <div className="flex items-center gap-1.5 mt-1.5">
          <span className={`w-1.5 h-1.5 rounded-full ${statusColor}`} />
          <span className="text-[10px] text-muted-foreground tracking-wide leading-tight">
            {statusLabel} <span className="text-slate-400">· rif. {range.lo}–{range.hi}%</span>
          </span>
        </div>
      </div>
    </div>
  );
};

// =================== container ===================
export const DerivedInsightsPanel = () => {
  const [isClinician, setIsClinician] = useState(false);
  useEffect(() => {
    const refresh = () => api.get("/auth/me")
      .then((r) => setIsClinician(effectiveIsClinician(r.data?.is_clinician)))
      .catch(() => {});
    refresh();
    const onChange = () => refresh();
    window.addEventListener("revela:forced-user-view-changed", onChange);
    return () => window.removeEventListener("revela:forced-user-view-changed", onChange);
  }, []);
  return (
    <div className="space-y-3" data-testid="derived-insights-panel">
      <h3 className="text-base font-semibold flex items-center gap-2 pt-2">
        <Sparkles className="w-4 h-4 text-rose-500" />
        Indicatori derivati
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <WellbeingGauge isClinician={isClinician} />
        <WeeklySummaryCard />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <EquanimityCard />
        <CircumplexCard isClinician={isClinician} />
      </div>
      {/* 2026-05-30 — Karasek card removed: redundant given the 60 clinical
          configurations (work + 4 social contexts) that already integrate
          carico × controllo × energia × piacevolezza. Karasek's 4-quadrant
          reading was clinically misleading without the affective axis. */}
      <WorkPatternsCard isClinician={isClinician} />
      <SocialPatternsCard context="famiglia" isClinician={isClinician} />
      <AmbivalenceCard context="famiglia" isClinician={isClinician} />
      <SocialPatternsCard context="amici" isClinician={isClinician} />
      <AmbivalenceCard context="amici" isClinician={isClinician} />
      <SocialPatternsCard context="partner" isClinician={isClinician} />
      <AmbivalenceCard context="partner" isClinician={isClinician} />
      <SocialPatternsCard context="solo" isClinician={isClinician} />
      <AmbivalenceCard context="solo" isClinician={isClinician} />
      {/* 2026-05-30 — SDT radar removed (Profilo motivazionale): no EMA-normative
          cutoffs exist, the 3 dimensions are already implicit in the 60 clinical
          patterns, and the radar lacks the affective axis (P × E). Clinically
          confusing without added value. */}
      <TrendRecoveryCard />
    </div>
  );
};

export default DerivedInsightsPanel;
